import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Condition } from 'Component/others';
import { useModal } from 'Store/modal';
import useGetPresences from 'Domain/host-attendance-record/useGetPresences';
import getUserLocation from 'View/host-attendance-record/helper/getUserLocation';
import AttendanceBanner from 'View/host-attendance-record/components/attendance-banner';
import RecordAttendance from 'View/host-attendance-record/section/record-attendance';
import FormAttendance from 'View/host-attendance-record/section/form-attendance';
import ProgressHeader from 'Component/progress-header';
import useBackModalRestriction from 'View/host-attendance-record/hooks/useBackModalRestriction';
import HostAttendanceRecordProvider, { useHostAttendanceRecordState } from 'View/host-attendance-record/state';

import styles from './styles.module.css';

const HostAttendanceRecord = () => {
  const { t } = useTranslation('host-attendance');
  const navigate = useNavigate();
  const { state, updateState } = useHostAttendanceRecordState();
  const { onBackRestriction } = useBackModalRestriction();
  const { onOpenModal } = useModal();
  const permissionDeniedShown = useRef(false);

  const { data: presenceData, isPending: isPresencePending } = useGetPresences();

  const { isClockedIn } = presenceData ?? {};

  const handlePermissionDenied = (force = false) => {
    updateState({ isPermissionDenied: true });
    if (!force && permissionDeniedShown.current) return;
    permissionDeniedShown.current = true;
    onOpenModal({
      width: '180px',
      maxWidth: '300px',
      isOpen: true,
      type: 'confirm',
      title: t('capture_photo.permission_denied.title'),
      contentHtmlDescription: t('capture_photo.permission_denied.description'),
      showPrimaryButton: false,
      showSecondaryButton: false,
    });
  };

  const handleBack = () => {
    state?.currentStep === 0 && navigate('/host');
    state?.currentStep === 1 && updateState({ currentStep: state?.currentStep - 1, capturedImage: { first: null, second: null }, tempCapturedImage: null });
    state?.currentStep === 2 &&
      updateState({ currentStep: state?.currentStep - 2, capturedImage: { first: null, second: null }, tempCapturedImage: null, notes: '' });
  };

  useEffect(() => {
    getUserLocation().catch((error) => {
      if (error.code === 1) handlePermissionDenied();
    });
  }, []); // eslint-disable-line

  return (
    <Box className={styles['host-attendance-record__container']}>
      <ProgressHeader
        currentStep={state?.currentStep}
        onBackHandler={state?.currentStep === 2 ? () => onBackRestriction({ submit: handleBack }) : handleBack}
      />
      <AttendanceBanner isClockedIn={isClockedIn} />
      <Condition statement={state?.currentStep === 2} fallback={<RecordAttendance onPermissionDenied={handlePermissionDenied} />}>
        <Condition statement={!isPresencePending}>
          <FormAttendance presenceData={presenceData} />
        </Condition>
      </Condition>
    </Box>
  );
};

const HostAttendanceRecordWrapper = () => <HostAttendanceRecordProvider children={<HostAttendanceRecord />} />;

export default HostAttendanceRecordWrapper;
